/**
 * Game API Service
 *
 * Client-side API functions for game actions.
 * Handles HTTP requests to backend game endpoints.
 */

import { post, get } from '@/lib/api-client';
import type {
  GameStateRedis,
  RollDiceResponse,
  ReleasePawnResponse,
  MovePawnResponse,
  LegalMove
} from 'shared-types';

/**
 * Custom error interface for API errors
 */
interface ApiError extends Error {
  statusCode?: number;
  code?: string;
  /** Parsed response body preserved by the api-client (Story 04-04). */
  body?: unknown;
}

/**
 * Whether an error is the server's `409 STALE_TURN` (Story 02-02 / 02-03): the
 * action targeted a turn instance the game has already moved past. Distinct from
 * a generic 409 (e.g. `ALREADY_ROLLED`, which the roll-dice path owns) and from
 * a 403 (not your turn) — a `STALE_TURN` means "your turn, but it advanced", and
 * the client recovers by resyncing rather than surfacing an error.
 */
function isStaleTurnError(error: unknown): boolean {
  const apiError = error as ApiError;
  return apiError.statusCode === 409 && apiError.code === 'STALE_TURN';
}

/**
 * `movePawn` result: the server's `MovePawnResponse` plus `staleTurn`, set when
 * the move was rejected as targeting a superseded turn (409 STALE_TURN). Kept
 * distinct from the `success: false` 403 outcome — they mean different things
 * (turn moved on vs. not your turn) and the caller handles them differently.
 */
export interface MovePawnResult extends Omit<MovePawnResponse, 'turnEpoch'> {
  staleTurn?: boolean;
  /**
   * Present only when the server accepted the action. Absent on a handled
   * rejection (403 / 409 STALE_TURN), which advances nothing — the resync does —
   * and absent against a pre-BUG-20260721-01 server that does not send it.
   * `applyIfFresh(undefined, …)` already means "apply, do not advance", so both
   * cases degrade to the previous behaviour instead of freezing the board.
   */
  turnEpoch?: number;
}

/**
 * `releasePawn` result: `ReleasePawnResponse` plus a distinct `staleTurn` flag
 * for a 409 STALE_TURN rejection (see `MovePawnResult`).
 */
export interface ReleasePawnResult extends Omit<ReleasePawnResponse, 'turnEpoch'> {
  staleTurn?: boolean;
  /** See {@link MovePawnResult.turnEpoch}. */
  turnEpoch?: number;
}

/**
 * `advanceTurn` result. `advanceTurn` returns no body, so this only carries the
 * `staleTurn` signal for a 409 STALE_TURN rejection; an empty object means the
 * advance succeeded (or was silently ignored as a 403).
 */
export interface AdvanceTurnResult {
  staleTurn?: boolean;
}

/**
 * Idempotency payload for a single user-initiated roll (Story 04-04).
 *
 * `rollId` is generated once at the call site and reused verbatim on any 401
 * refresh replay, so the server can dedupe a duplicate delivery. `expectedTurnEpoch`
 * lets the server reject a roll aimed at a superseded turn (409 STALE_TURN).
 */
export interface RollDiceParams {
  rollId: string;
  expectedTurnEpoch: number;
  debugDiceValue?: number;
}

/**
 * Discriminated outcome of a roll (Story 04-04). The roll is self-confirming:
 * the authoritative value returns in the HTTP body rather than only over Ably,
 * and the two `409`s are handled results — not thrown errors — so the caller can
 * reconcile them silently while counting them.
 */
export type RollDiceOutcome =
  | { status: 'rolled'; diceValue: number; legalMoves: LegalMove[]; turnEpoch: number }
  | { status: 'already-rolled'; diceValue: number; legalMoves: LegalMove[]; turnEpoch: number }
  | { status: 'stale-turn' }
  | { status: 'third-consecutive-six' }
  | { status: 'not-your-turn' };

/**
 * Roll dice for the current turn player.
 *
 * Sends the idempotency payload and marks the request replay-safe so 04-01's
 * 401-refresh retry reuses the same `rollId`. Returns the authoritative body as
 * a `rolled` outcome; both `409`s and the silent `403`/`THIRD_CONSECUTIVE_SIX`
 * are mapped to handled outcomes. Truly unexpected failures are re-thrown.
 *
 * @param gameId - The game identifier
 * @param params - The roll's idempotency payload (+ optional debug dice value)
 */
export async function rollDice(gameId: string, params: RollDiceParams): Promise<RollDiceOutcome> {
  try {
    const body = await post<RollDiceResponse>(`/games/${gameId}/roll-dice`, params, {
      idempotent: true
    });
    return {
      status: 'rolled',
      diceValue: body.diceValue,
      legalMoves: body.legalMoves,
      turnEpoch: body.turnEpoch
    };
  } catch (error) {
    const apiError = error as ApiError;

    // 403: not this player's turn — reconciled silently, no dice applied.
    if (apiError.statusCode === 403) {
      return { status: 'not-your-turn' };
    }

    // 409 ALREADY_ROLLED: the authoritative dice payload is echoed in the body,
    // so the roller converges to it without surfacing an error or a second dice.
    if (apiError.statusCode === 409 && apiError.code === 'ALREADY_ROLLED') {
      const body = apiError.body as
        | { diceValue?: unknown; legalMoves?: unknown; turnEpoch?: unknown }
        | undefined;
      if (typeof body?.diceValue === 'number' && typeof body.turnEpoch === 'number') {
        return {
          status: 'already-rolled',
          diceValue: body.diceValue,
          legalMoves: Array.isArray(body.legalMoves) ? (body.legalMoves as LegalMove[]) : [],
          turnEpoch: body.turnEpoch
        };
      }
      // Body missing (older server / stripped by a proxy): fall back to a resync.
      return { status: 'stale-turn' };
    }

    // 409 STALE_TURN: the turn moved on; the caller resyncs authoritative state.
    if (apiError.statusCode === 409 && apiError.code === 'STALE_TURN') {
      return { status: 'stale-turn' };
    }

    // THIRD_CONSECUTIVE_SIX (400): the backend already broadcast dice-rolled(6)
    // over Ably before forfeiting the turn; that event drives display + forfeit.
    if (apiError.code === 'THIRD_CONSECUTIVE_SIX') {
      return { status: 'third-consecutive-six' };
    }

    // Truly unexpected (network / timeout / 500): surface to the caller.
    throw error;
  }
}

/**
 * Release a pawn from base to start position
 * Called when player clicks a pawn after rolling a 6
 *
 * @param gameId - The game identifier
 * @param pawnIndex - Index of the pawn to release (0-3)
 * @param debugReleasePawn - Optional debug flag for testing (only used in development)
 * @returns Promise with release result including new position
 * @throws Error if request fails (except 403 which is handled silently)
 */
export async function releasePawn(
  gameId: string,
  pawnIndex: number,
  debugReleasePawn?: boolean,
  expectedTurnEpoch?: number
): Promise<ReleasePawnResult> {
  try {
    return await post<ReleasePawnResponse>(`/games/${gameId}/release-pawn`, {
      pawnIndex,
      debugReleasePawn,
      // Epoch contract (Story 04-05): send the turn instance we acted on so the
      // server (Story 02-03) can reject a release aimed at a superseded turn.
      // Omitted (undefined) on an old client is harmless — the server skips the
      // assertion.
      expectedTurnEpoch
    });
  } catch (error) {
    const apiError = error as ApiError;
    // Handle 403 silently - return failed response when not player's turn
    if (apiError.statusCode === 403) {
      return {
        success: false,
        pawnIndex,
        newPosition: -1
      };
    }
    // Distinct 409 STALE_TURN outcome (Story 04-05): the turn moved on. The
    // caller resyncs + counts rather than surfacing an error. Not collapsed with
    // 403 — a different meaning and recovery.
    if (isStaleTurnError(error)) {
      return {
        success: false,
        pawnIndex,
        newPosition: -1,
        staleTurn: true
      };
    }
    throw error;
  }
}

/**
 * Move a pawn on the board
 * Called when player clicks a pawn to move it after rolling dice
 *
 * @param gameId - The game identifier
 * @param pawnIndex - Index of the pawn to move (0-3)
 * @param fromPosition - Current position of the pawn (string format, e.g., 'GS2')
 * @param toPosition - Target position of the pawn (string format, e.g., 'GS6')
 * @returns Promise with move result including capture info and bonus turn status
 * @throws Error if request fails (except 403 which is handled silently)
 */
export async function movePawn(
  gameId: string,
  pawnIndex: number,
  fromPosition: string,
  toPosition: string,
  expectedTurnEpoch?: number,
  expectedDiceValue?: number
): Promise<MovePawnResult> {
  try {
    return await post<MovePawnResponse>(`/games/${gameId}/move-pawn`, {
      pawnIndex,
      fromPosition,
      toPosition,
      // Epoch + dice contract (Story 04-05): send the turn instance and dice
      // value we acted on so the server (Story 02-02) can reject a move whose
      // turn or dice moved on under us. Both optional on the wire — an old
      // server ignores them; a new server skips the assertion when omitted.
      expectedTurnEpoch,
      expectedDiceValue
    });
  } catch (error) {
    const apiError = error as ApiError;
    // Handle 403 silently - return failed response when not player's turn
    if (apiError.statusCode === 403) {
      return {
        success: false,
        pawnIndex,
        fromPosition,
        toPosition,
        grantBonusTurn: false
      };
    }
    // Distinct 409 STALE_TURN outcome (Story 04-05): our turn advanced. The
    // caller restores the pawn + resyncs + counts, surfacing no error. Kept
    // separate from 403 (not your turn) and from other 409s (e.g. ALREADY_ROLLED,
    // owned by the roll path), which still throw.
    if (isStaleTurnError(error)) {
      return {
        success: false,
        pawnIndex,
        fromPosition,
        toPosition,
        grantBonusTurn: false,
        staleTurn: true
      };
    }
    throw error;
  }
}

/**
 * Get current game state from server
 *
 * The response includes `turnEpoch` (Story 01-01) — the authoritative turn
 * instance identity used by the reconnect resync (Story 04-02) to decide
 * whether an incoming snapshot is newer than the client's store.
 *
 * @param gameId - The game identifier
 * @returns Promise with game state including current turn info and `turnEpoch`
 * @throws Error if request fails
 */
export async function getGameState(gameId: string): Promise<GameStateRedis> {
  return get<GameStateRedis>(`/games/${gameId}/state`);
}

/**
 * Advance turn to the next player
 * Called when current player's turn ends
 *
 * @param gameId - The game identifier
 * @returns Promise that resolves when turn is advanced
 * @throws Error if request fails (except 403 which is handled silently)
 */
export async function advanceTurn(
  gameId: string,
  expectedTurnEpoch?: number
): Promise<AdvanceTurnResult> {
  try {
    // Epoch contract (Story 04-05): carry the turn instance we acted on so the
    // server (Story 02-03) can reject a stale advance (e.g. one fired on
    // no-legal-moves after the turn already advanced). Optional on the wire.
    await post(`/games/${gameId}/advance-turn`, { expectedTurnEpoch });
    return {};
  } catch (error) {
    const apiError = error as ApiError;
    // 403 (not your turn) stays silent — a no-op, not a failure.
    if (apiError.statusCode === 403) {
      return {};
    }
    // Distinct 409 STALE_TURN: the turn moved on. Signal the caller to resync +
    // count rather than surfacing an error.
    if (isStaleTurnError(error)) {
      return { staleTurn: true };
    }
    throw error;
  }
}

/**
 * Exit automove mode and return to manual play
 * Called when player clicks "Return to Manual" button
 *
 * @param gameId - The game identifier
 * @returns Promise that resolves when automove mode is exited
 * @throws Error if request fails
 */
export async function exitAutomove(gameId: string): Promise<void> {
  await post(`/games/${gameId}/exit-automove`, {});
}

/**
 * Exit early after winning (1st place in 4-player game)
 * Clears active-game mapping so player can join new games
 * Player still receives rewards when game ends via server-side notifySokaApiMatchComplete
 *
 * @param gameId - The game identifier
 * @returns Promise that resolves when early exit is processed
 * @throws Error if request fails (e.g., player hasn't finished)
 */
export async function exitEarly(gameId: string): Promise<void> {
  await post(`/games/${gameId}/exit-early`, {});
}

/**
 * Notify backend that a player has left the game
 * Called when Ably presence detects a player leaving
 * Puts the player into automove mode for potential reconnection
 *
 * @param gameId - The game identifier
 * @param playerId - The player who left (can be self or another player observed via presence)
 * @returns Promise that resolves when notification is sent
 * @throws Error if request fails
 */
export async function notifyPlayerLeft(gameId: string, playerId: string): Promise<void> {
  await post(`/games/${gameId}/player-left`, { playerId });
}

/**
 * Voluntarily leave an active game
 * - For paid games: Clear active-game mapping (can't rejoin), add to automove
 * - For free games: Just add to automove (can rejoin later)
 *
 * @param gameId - The game identifier
 * @returns Promise that resolves when leave is processed
 * @throws Error if request fails
 */
export async function leaveGame(gameId: string): Promise<void> {
  await post(`/games/${gameId}/leave`, {});
}

/**
 * Send an emoji reaction to all players in the game
 *
 * @param gameId - The game identifier
 * @param emoji - Emoji key to send
 * @returns Promise that resolves when reaction is sent
 */
export async function sendEmojiReaction(gameId: string, emoji: string): Promise<void> {
  await post(`/games/${gameId}/emoji-reaction`, { emoji });
}

/**
 * Response from the active game endpoint
 */
export interface ActiveGameResponse {
  gameId: string;
  gameType: 'free' | 'paid';
}

/**
 * Get the current user's active game (if any)
 * Used for rejoin functionality when user returns to landing page
 *
 * @returns Promise with active game info, or null if no active game
 */
export async function getActiveGame(): Promise<ActiveGameResponse | null> {
  try {
    return await get<ActiveGameResponse>('/games/active-game');
  } catch (error) {
    const apiError = error as ApiError;
    // 404 means no active game - return null instead of throwing
    if (apiError.statusCode === 404) {
      return null;
    }
    throw error;
  }
}
